import { listTablePrefs, TablePrefsParams } from 'app/constants'
import StatsCell from 'app/plugins/infrastructure/components/common/cells/StatsCell'
import OpenstackClusterFilterDropdown from 'app/plugins/openstack/components/common/OpenstackClusterFilterDropdown'
import { ArrayElement } from 'core/actions/Action'
import InferActionParams from 'core/actions/InferActionParams'
import DocumentMeta from 'core/components/DocumentMeta'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import { GridBatchActionSpec } from 'core/elements/grid/hooks/useGridSelectableRows'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'
import { HeaderRefreshPortal } from 'core/elements/header/portals'
import Text from 'core/elements/Text'
import useGlobalParams from 'core/hooks/useGlobalParams'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { routeManager } from 'core/utils/routes'
import { createResourceLabelsCell } from 'k8s/components/common/entity/labels-and-annotations/helpers'
import DataKeys from 'k8s/DataKeys'
import { pick } from 'ramda'
import { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { gridSearchFn } from '../../images/helpers'
import { formatTimestampToLocal } from '../../vms/helpers'
import { deleteHostAggregate, listHostAggregates, listResmgrHosts } from '../actions'
import { hostAggregatesSelector, UNASSIGNED_CLUSTER } from '../selectors'
import AddHostAggregateModal from './AddHostAggregateModal'
import EditHostAggregateModal from './EditHostAggregateModal'
import { Item } from 'core/elements/dropdown/GlobalDropdownWithSearch'

type ModelDataKey = DataKeys.HostAggregates
type SelectorModel = ArrayElement<ReturnType<typeof hostAggregatesSelector>>
type ActionParams = InferActionParams<typeof listHostAggregates>
// @fixme using a type here because of https://github.com/microsoft/TypeScript/issues/15300
type Params = ActionParams & {}

const defaultParams: Params & SortingState = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'HostAggregates',
  listTablePrefs,
)

const searchTargets = ['name', 'id', 'clusterName', 'hostInfo', 'metadata']
const searchFunctions = [
  {
    property: 'hostInfo',
    searchFn: (value, searchString) => {
      return value.some((host) => {
        return (
          host?.name?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase()) ||
          host?.ip?.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
        )
      })
    },
  },
  {
    property: 'metadata',
    searchFn: gridSearchFn,
  },
]

export const HostsCellComponent = ({ value }) => {
  return (
    <>
      {value.map((host) => (
        <Text variant="body2">
          {host.name} ({host.ip})
        </Text>
      ))}
    </>
  )
}

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
  },
  {
    key: 'id',
    label: 'ID',
    display: false,
  },
  {
    key: 'clusterName',
    label: 'Cluster',
  },
  {
    key: 'hostInfo',
    label: 'Hosts',
    CellComponent: HostsCellComponent,
  },
  {
    key: 'physicalNetworkLabels',
    label: 'Physical Network Label',
    display: false,
    CellComponent: (props) => {
      const physicalNetworkLabels = props?.value || []
      return <Text variant="body2"> {physicalNetworkLabels?.join(', ')}</Text>
    },
  },
  {
    key: 'usage',
    label: 'Resource Utilization',
    CellComponent: StatsCell,
  },
  {
    key: 'metadata',
    label: 'Metadata',
    disableSorting: true,
    CellComponent: createResourceLabelsCell({ type: 'table', separator: '=' }),
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

export default function HostAggregatesListPage() {
  const [selectedHostAggregates, setSelectedHostAggregates] = useState([])
  const [showEditHostAggregateModal, setShowEditHostAggregateModal] = useState(false)
  const [isPf9ManagedHostAggregate, setIsPf9ManagedHostAggregate] = useState(false)
  const [filteredHostAggregates, setFilteredHostAggregates] = useState<SelectorModel[]>([])

  // const [showToggleHaModal, setShowToggleHaModal] = useState(false)

  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)

  // Idk why the dependency isn't working, call it explicitly here
  useListAction(listResmgrHosts, { params })
  const { message, loading, reload } = useListAction(listHostAggregates, {
    params,
  })
  const hostAggregates = useSelector(hostAggregatesSelector)

  // useEffect(() => {
  //   const aggregatesWithAz = hostAggregates.filter(
  //     (hostAggregate) => !!hostAggregate.availability_zone,
  //   )
  //   const aggregateId = aggregatesWithAz?.[0]?.id
  //   const makeCalls = async () => {
  //     const haConfig = await getHostAggregateConfig(aggregateId)
  //     const haEnabled = !!haConfig?.status?.[0]?.enabled
  //   }
  //   makeCalls()
  // }, [hostAggregates])

  const batchActions: GridBatchActionSpec<SelectorModel>[] = [
    {
      cond: (hosts) => {
        return hosts.length > 0 && hosts[0]?.name !== 'compute'
      },
      disabledTooltip: 'Compute host aggregate cannot be edited',
      label: 'Edit Host Aggregate',
      icon: 'edit',
      handleAction: (selected) => {
        setSelectedHostAggregates(selected)
        setShowEditHostAggregateModal(true)
      },
      refreshAfterSuccess: true,
    },
    // {
    //   cond: (hosts) => {
    //     return hosts.length > 0
    //   },
    //   label: 'Toggle High Availability',
    //   icon: 'bolt-auto',
    //   handleAction: (selected) => {
    //     setSelectedHostAggregates(selected)
    //     setShowToggleHaModal(true)
    //   },
    //   refreshAfterSuccess: true,
    // },
  ]

  return (
    <>
      <DocumentMeta title="Host Aggregates" />
      <HeaderRefreshPortal>
        <OpenstackClusterFilterDropdown
          data={hostAggregates}
          setFilteredData={setFilteredHostAggregates}
        />
      </HeaderRefreshPortal>
      <>
        <AddHostAggregateModal addRoute={routeManager.routes.openstack.createHostAggregate} />
        {showEditHostAggregateModal && (
          <EditHostAggregateModal
            onClose={() => setShowEditHostAggregateModal(false)}
            rows={selectedHostAggregates}
          />
        )}
        {/* {showToggleHaModal && (
          <ToggleHaDialog
            onClose={() => setShowToggleHaModal(false)}
            rows={selectedHostAggregates}
          />
        )} */}
        <ListContainer<ModelDataKey, SelectorModel>
          dataKey={DataKeys.HostAggregates}
          searchTargets={searchTargets}
          searchFunctions={searchFunctions}
          uniqueIdentifier="id"
          addUrl={routeManager.routes.openstack.createHostAggregate.path()}
          addText="Add Host Aggregate"
          loading={loading}
          loadingMessage={message}
          onRefresh={reload}
          data={filteredHostAggregates}
          columns={columns}
          getParamsUpdater={getParamsUpdater}
          batchActions={batchActions}
          deleteAction={deleteHostAggregate}
          deleteCond={([hostAggregate]) => {
            setIsPf9ManagedHostAggregate(hostAggregate?.metadata?.['pf9-managed'])
            return (
              !hostAggregate?.hosts?.length &&
              hostAggregate?.name !== 'compute' &&
              !hostAggregate?.metadata?.['pf9-managed']
            )
          }}
          deleteDisabledInfo={
            isPf9ManagedHostAggregate
              ? 'PCD managed host aggregate cannot be deleted'
              : 'Host Aggregate must not have any hosts in order to be deleted'
          }
          {...pick(listTablePrefs, params)}
        />
      </>
    </>
  )
}
